import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { cloneDeep } from "lodash";
import { OopsComponentFormBase } from "src/app/core/base/oops-component-form-base";
import { FocusingDirective } from "src/app/shared/ui/forms/inputs/focusing.directive";
import { GeneralFormOption } from "./general-form-config";
import { select2Status } from "../../shared/select2-configuration";
import { StatusReferenceModel, VehicleGroupReferenceModel, VehicleTypeReferenceModel } from "src/app/core/models/reference-model/reference-general-model";
import { VehicleCompositionCommandModel, VehicleCompositionModel } from "src/app/core/models/vehicle-model/vehicle-composition";
import { DateConverterService } from "src/app/core/utils/date-converter.service";
import { VehicleCompositionService } from "src/app/core/services/vehicle-services";
import { SecurityGroupSecurityModel } from "src/app/core/models/security-model/security-group-security.model";

@Component({
    selector: 'op-vehicle-composition-detail-general',
    templateUrl: './general.component.html'
})
export class VehicleCompositionGeneralDetailComponent extends OopsComponentFormBase implements OnChanges {
    readonly ERROR_CODE_REQUIRED = 'Code is required.';
    readonly ERROR_CODE_FORMAT = 'Code wrong format.';
    readonly ERROR_CODE_DUPLICATE = 'This code is duplicate.';
    readonly ERROR_NAME_REQUIRED = 'Name is required.';
    readonly ERROR_VEHICLE_GROUP_REQUIRED = 'Vehicle group is required.';
    readonly ERROR_VEHICLE_TYPE_REQUIRED = 'Vehicle type is required.';
    readonly ERROR_STATUS_REQUIRED = 'Status is required.';

    @Input() id: string;
    @Input() vehicleGroupReferences: VehicleGroupReferenceModel[];
    @Input() vehicleTypeReferences: VehicleTypeReferenceModel[];
    @Input() statusReferences: StatusReferenceModel[];
    @Input() vehicleComposition = {} as VehicleCompositionModel;
    @Input() userSecurity: SecurityGroupSecurityModel
    @Output() formStatus = new EventEmitter<boolean>();
    @ViewChild(FocusingDirective) focusingDirective: FocusingDirective;
    public vehicleTypeReferenceFilters: VehicleTypeReferenceModel[] = new Array();
    public statusOption = select2Status;
    public focusing: boolean = false;

    constructor(formBuilder: UntypedFormBuilder,
        private dateConverterService: DateConverterService,
        private vehicleCompositionService: VehicleCompositionService) {
        super(formBuilder);
    }

    initForm() {
        this.formSetup();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['vehicleComposition'] || changes['vehicleTypeReferences']) {
            this.formSetup();
        }
    }

    formSetup() {
        this.formGroup = new UntypedFormGroup(cloneDeep(GeneralFormOption));
        if (!this.vehicleComposition) {
            this.completeProcessing();
            this.formGroup.reset();
            this.fillDefaultValue();
        } else {
            this.fillModelToForm(this.vehicleComposition);
        }
        this.formGroup.statusChanges
            .subscribe(val => this.onFormStatusChange());
    }

    private onFormStatusChange() {
        if (this.processing) {
            this.formStatus.emit(this.formGroup.valid);
        }
    }

    private fillModelToForm(model: VehicleCompositionModel) {
        this.fillterVehicleType(model.vehicleGroupCode);
        this.formGroup.patchValue({
            code: model.compositionCode,
            name: model.compositionName,
            vehicleGroup: model.vehicleGroupCode,
            vehicleType: model.vehicleTypeCode,
            status: model.statusCode,
            commitByName: model.commitByName,
            commitDateTime: this.dateConverterService.convertDateTime24(model.commitDateTime)
        });
    }

    valueChange(value, name) {
        this.formGroup.controls[name].setValue(value);
    }

    validateForm(): boolean {
        this.startProcessing();
        if (!this.validForm()) {
            return false;
        }
        return true;
    }

    getErrorMessageForm(): string {
        if (!this.formGroup.controls["code"].valid) {
            return this.getCodeErrorMessage();
        } else if (!this.formGroup.controls["name"].valid) {
            return this.ERROR_NAME_REQUIRED;
        } else if (!this.formGroup.controls["vehicleGroup"].valid) {
            return this.ERROR_VEHICLE_GROUP_REQUIRED;  
        } else if (!this.formGroup.controls["vehicleType"].valid) {
            return this.ERROR_VEHICLE_TYPE_REQUIRED;                       
        } else if (!this.formGroup.controls["status"].valid) {
            return this.ERROR_STATUS_REQUIRED;
        }
    }

    private getCodeErrorMessage(): string {
        if (this.formGroup.controls["code"].errors?.required) {
            return this.ERROR_CODE_REQUIRED;
        } else if (this.formGroup.controls["code"].errors?.minlength || this.formGroup.controls["code"].errors?.pattern) {
            return this.ERROR_CODE_FORMAT;
        } else if (this.formGroup.controls["code"].errors?.duplicate) {
            return this.ERROR_CODE_DUPLICATE;
        }
    }

    getValue(): VehicleCompositionCommandModel {
        let formValue = this.formGroup.getRawValue();
        let addCommand: VehicleCompositionCommandModel = {
            vehicleCompositionId: this.id,
            compositionCode: formValue.code,
            compositionName: formValue.name,
            vehicleTypeCode: formValue.vehicleType,
            statusCode: formValue.status,
            vehicleCompositionConfigurations: []
        }
        return addCommand;
    }

    onCodeChange() {
        if (this.formGroup.get("code").value != null)
            this.formGroup.get("code").setValue(this.formGroup.get("code").value.toUpperCase());
    }

    validateDuplicateCode(e) {
        if (e.target.value == "") {
            return;
        }
        this.vehicleCompositionService.getByCode(e.target.value)
            .subscribe(
                (responses: VehicleCompositionModel[]) => {
                    this.checkDuplicate(responses);
                }
            )
    }

    private checkDuplicate(responses: VehicleCompositionModel[]) {
        if (responses) {
            let filter = responses.filter(x => x.vehicleCompositionId != this.id);
            if (filter?.length) {
                this.formGroup.get('code').setErrors({ duplicate: true });
            }
            else if (this.formGroup.controls["code"].errors?.duplicate) {
                delete this.formGroup.controls["code"].errors['duplicate'];
                this.formGroup.controls["code"].updateValueAndValidity();
            }
        }
    }

    vehicleGroupChange(value, name) {
        this.formGroup.controls[name].setValue(value);
        this.fillterVehicleType(value);
    }

    private fillterVehicleType(vehicleGroupCode : string) {
        if (vehicleGroupCode && this.vehicleTypeReferences) {
            this.vehicleTypeReferenceFilters = this.vehicleTypeReferences.filter(x => x.vehicleGroupCode == vehicleGroupCode);
            if (this.vehicleTypeReferenceFilters?.length) {
                this.formGroup.controls['vehicleType'].setValue(this.vehicleTypeReferenceFilters[0].vehicleTypeCode);
                return;
            }
        }
        this.vehicleTypeReferenceFilters = [];
        this.formGroup.controls['vehicleType'].setValue(null);
    }

    private fillDefaultValue() {
        this.formGroup.patchValue({
            status: "A"
        });
    }
}